import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { toText } from '../lib/toText';

export interface TimeOffBlock {
  id: string;
  agent_id: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
  created_at: string;
}

export interface CreateTimeOffResponse {
  block: TimeOffBlock;
  orphaned_appointment_ids: string[];
}

export interface AvailabilityRule {
  id: string;
  /** ISO weekday: 1=Monday..7=Sunday */
  day_of_week: number;
  /** "HH:MM" or "HH:MM:SS" — backend Pydantic Time accepts both */
  start_time: string;
  end_time: string;
  is_active: boolean;
}

export interface AvailabilityRuleInput {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active?: boolean;
}

export interface BookableSlot {
  starts_at: string;   // ISO with offset
  ends_at: string;
}

export interface MyAgentSlotsResponse {
  agent: { id: string; first_name: string; last_name: string };
  timezone: string;
  slot_duration_minutes: number;
  slots: BookableSlot[];
}

export interface BookAppointmentRequest {
  starts_at: string;            // ISO datetime — must be tz-aware
  purpose?: string | null;
  case_id?: string | null;
}

export interface AppointmentParty {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
}

export interface AppointmentSummary {
  id: string;
  agent: AppointmentParty | null;
  original_agent: AppointmentParty;
  member: AppointmentParty;
  case_id: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  purpose: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  is_orphaned: boolean;
  needs_my_action: boolean;
}

export interface AppointmentListResponse {
  items: AppointmentSummary[];
  total: number;
  page: number;
  size: number;
}

export interface ListAppointmentsFilters {
  /** ISO datetime, inclusive on the start side */
  from?: string;
  /** ISO datetime, exclusive on the end side */
  to?: string;
  /** Repeated server-side; default = ['scheduled'] when omitted. */
  status?: string[];
  /** 'me' (current user), 'null' / 'unassigned' / 'orphaned', or a UUID */
  assigned_to?: string;
  case_id?: string;
  page?: number;
  size?: number;
}

class SchedulingService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  // ─── Booking (client-side) ───────────────────────────────────────
  async listMyAgentSlots(opts?: { from?: string; to?: string }): Promise<MyAgentSlotsResponse> {
    const params: Record<string, string> = {};
    if (opts?.from) params.from = opts.from;
    if (opts?.to) params.to = opts.to;
    const res = await this.api.get<MyAgentSlotsResponse>(
      '/api/v1/scheduling/my-agent/slots',
      { params },
    );
    return res.data;
  }

  async bookAppointment(body: BookAppointmentRequest): Promise<AppointmentSummary> {
    const res = await this.api.post<AppointmentSummary>(
      '/api/v1/scheduling/appointments',
      body,
    );
    return res.data;
  }

  // ─── Appointments queue + triage ─────────────────────────────────
  async listAppointments(filters: ListAppointmentsFilters = {}): Promise<AppointmentListResponse> {
    // Same paramsSerializer pattern as workflow.service so repeated keys
    // (status[]) come through as ?status=a&status=b that FastAPI parses.
    const res = await this.api.get<AppointmentListResponse>(
      '/api/v1/scheduling/appointments',
      {
        params: filters,
        paramsSerializer: (params) => {
          const parts: string[] = [];
          for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) continue;
            if (Array.isArray(value)) {
              for (const v of value) {
                if (v === undefined || v === null) continue;
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
              }
            } else {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(toText(value))}`);
            }
          }
          return parts.join('&');
        },
      },
    );
    return res.data;
  }

  async cancelAppointment(id: string, reason?: string | null): Promise<AppointmentSummary> {
    const res = await this.api.patch<AppointmentSummary>(
      `/api/v1/scheduling/appointments/${id}/cancel`,
      { reason: reason || null },
    );
    return res.data;
  }

  /** Record a scheduled appointment's outcome after the call: 'completed'
   *  (it happened) or 'no_show' (the client didn't show). Owning agent or a
   *  manager only; the server's `status='scheduled'` guard prevents a
   *  double-disposition. */
  async dispositionAppointment(
    id: string,
    outcome: 'completed' | 'no_show',
  ): Promise<AppointmentSummary> {
    const res = await this.api.patch<AppointmentSummary>(
      `/api/v1/scheduling/appointments/${id}/disposition`,
      { outcome },
    );
    return res.data;
  }

  async claimAppointment(id: string): Promise<AppointmentSummary> {
    const res = await this.api.post<AppointmentSummary>(
      `/api/v1/scheduling/appointments/${id}/claim`,
    );
    return res.data;
  }

  async reassignAppointment(
    id: string,
    body: { new_agent_id: string; reason: string },
  ): Promise<AppointmentSummary> {
    const res = await this.api.patch<AppointmentSummary>(
      `/api/v1/scheduling/appointments/${id}/reassign`,
      body,
    );
    return res.data;
  }

  // ─── Availability (same surface for self + manager-on-behalf) ───
  //
  // URL is /agents/{agent_id}/availability — the `/me/availability`
  // aliases were removed alongside the time-off cleanup. Self-mode
  // callers resolve their own UUID via /auth/me cache.
  async listAgentAvailability(agentId: string): Promise<AvailabilityRule[]> {
    const res = await this.api.get<{ rules: AvailabilityRule[] }>(
      `/api/v1/scheduling/agents/${agentId}/availability`,
    );
    return res.data.rules;
  }

  async listMyAvailability(): Promise<AvailabilityRule[]> {
    const id = await authService.getMyId();
    return this.listAgentAvailability(id);
  }

  async replaceAgentAvailability(
    agentId: string,
    rules: AvailabilityRuleInput[],
  ): Promise<AvailabilityRule[]> {
    const res = await this.api.put<{ rules: AvailabilityRule[] }>(
      `/api/v1/scheduling/agents/${agentId}/availability`,
      { rules },
    );
    return res.data.rules;
  }

  async replaceMyAvailability(rules: AvailabilityRuleInput[]): Promise<AvailabilityRule[]> {
    const id = await authService.getMyId();
    return this.replaceAgentAvailability(id, rules);
  }

  // ─── Time-off — same surface for self + manager-on-behalf ──────
  // The legacy `listMyTimeOff` / `createMyTimeOff` / `deleteMyTimeOff`
  // helpers and their `/me/time-off` URL aliases were removed: the
  // backend exposes a single URL surface at `/agents/{agent_id}/time-off`
  // and the caller passes its own UUID (read once from /auth/me) when
  // acting on itself. Cleaner RBAC + zero duplication.
  async listAgentTimeOff(agentId: string): Promise<TimeOffBlock[]> {
    const res = await this.api.get<{ items: TimeOffBlock[] }>(
      `/api/v1/scheduling/agents/${agentId}/time-off`,
    );
    return res.data.items;
  }

  async createAgentTimeOff(
    agentId: string,
    body: { starts_at: string; ends_at: string; reason?: string | null },
  ): Promise<CreateTimeOffResponse> {
    const res = await this.api.post<CreateTimeOffResponse>(
      `/api/v1/scheduling/agents/${agentId}/time-off`,
      body,
    );
    return res.data;
  }

  async deleteAgentTimeOff(agentId: string, timeOffId: string): Promise<void> {
    await this.api.delete(
      `/api/v1/scheduling/agents/${agentId}/time-off/${timeOffId}`,
    );
  }
}

export default new SchedulingService();
